import { useEffect } from "react";

// Safari can throw this generic error when a fetch gets cancelled by a page navigation (e.g.
// leaving for Klarna). Same message a real failed request could show, so we only treat it as
// harmless if we know a redirect was just triggered (see markRedirectAttempt below).
const BENIGN_NAVIGATION_ABORT_PATTERN =
    /Load failed|Failed to fetch|NetworkError when attempting to fetch resource/i;

const REDIRECT_ATTEMPT_STORAGE_KEY = "adyen_redirect_attempt_at";
const REDIRECT_ATTEMPT_WINDOW_MS = 5000;

// Call right before starting a redirect payment (Klarna, Przelewy24, etc). Best-effort: a storage
// failure here (e.g. Safari private mode quota) must not stop component.handleAction(action) from
// running, since that's what actually starts the redirect.
export const markRedirectAttempt = (): void => {
    if (typeof window === "undefined") return;
    try {
        sessionStorage.setItem(REDIRECT_ATTEMPT_STORAGE_KEY, String(Date.now()));
    } catch {
        // Marker is best-effort; the redirect must still proceed.
    }
};

export const clearRedirectAttemptMarker = (): void => {
    if (typeof window === "undefined") return;
    try {
        sessionStorage.removeItem(REDIRECT_ATTEMPT_STORAGE_KEY);
    } catch {
        // Marker is best-effort; nothing to clean up if storage is unavailable.
    }
};

const wasRedirectJustTriggered = (): boolean => {
    if (typeof window === "undefined") return false;
    try {
        const markedAt = Number(sessionStorage.getItem(REDIRECT_ATTEMPT_STORAGE_KEY));
        return Boolean(markedAt) && Date.now() - markedAt < REDIRECT_ATTEMPT_WINDOW_MS;
    } catch {
        return false;
    }
};

// True only if this looks like the harmless "navigation cancelled the fetch" error AND we just
// triggered a redirect. Both must be true, so a real checkout error never gets hidden by mistake.
export const isNavigationAbortError = (error: Error | undefined | null): boolean => {
    return BENIGN_NAVIGATION_ABORT_PATTERN.test(error?.message ?? "") && wasRedirectJustTriggered();
};

// If the redirect never actually happens, don't leave the shopper on a blank page.
export const useNavigationAbortRecovery = (shouldRecover: boolean, reset: () => void): void => {
    useEffect(() => {
        if (!shouldRecover) return;
        const timer = setTimeout(() => reset(), 5000);
        return () => clearTimeout(timer);
    }, [shouldRecover, reset]);
};
